<script setup lang="ts">
/**
 * NotificationCenter - Notification management dropdown
 */

import type { Notification, NotificationType } from "@shared/types";
import { computed, onMounted, ref } from "vue";
import { useNotificationsStore } from "@/stores/notifications";

const notificationsStore = useNotificationsStore();

const isOpen = ref(false);
const activeTab = ref<"all" | "unread">("unread");

// Filter notifications by tab
const filteredNotifications = computed(() => {
  if (activeTab.value === "unread") {
    return notificationsStore.unreadNotifications;
  }
  return notificationsStore.notifications;
});

// Load notifications on mount
// WebSocket keeps them updated, stats are fetched only on NotificationsView
onMounted(async () => {
  await notificationsStore.fetchNotifications({ limit: 50 });
});

// Toggle dropdown
const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
};

// Close dropdown
const closeDropdown = () => {
  isOpen.value = false;
};

// Handle notification click
const handleNotificationClick = async (notification: Notification) => {
  if (notification.status === "unread") {
    await notificationsStore.markAsRead(notification.id);
  }

  if (notification.actionUrl) {
    window.location.href = notification.actionUrl;
  }

  closeDropdown();
};

// Mark all as read
const handleMarkAllAsRead = async () => {
  await notificationsStore.markAllAsRead();
};

// Delete notification
const handleDelete = async (id: number, event: MouseEvent) => {
  event.stopPropagation();

  if (confirm("Benachrichtigung löschen?")) {
    await notificationsStore.deleteNotification(id);
  }
};

// Get notification icon
const getNotificationIcon = (type: NotificationType): string => {
  switch (type) {
    case "job_completed":
      return "✓";
    case "job_failed":
      return "✗";
    case "new_result":
      return "📊";
    case "share_accessed":
      return "🔗";
    case "warning":
      return "⚠️";
    case "info":
      return "ℹ️";
    case "system":
      return "⚙️";
    default:
      return "🔔";
  }
};

// Get notification color
const getNotificationColor = (type: NotificationType): string => {
  switch (type) {
    case "job_completed":
      return "text-green-600";
    case "job_failed":
      return "text-red-600";
    case "new_result":
      return "text-blue-600";
    case "share_accessed":
      return "text-purple-600";
    case "warning":
      return "text-yellow-600";
    case "info":
      return "text-blue-500";
    case "system":
      return "text-gray-600";
    default:
      return "text-gray-600";
  }
};

// Format timestamp
const formatTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Gerade eben";
  if (diffMins < 60) return `vor ${diffMins} Min`;
  if (diffHours < 24) return `vor ${diffHours} Std`;
  if (diffDays < 7) return `vor ${diffDays} Tag${diffDays > 1 ? "en" : ""}`;

  return date.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "Europe/Berlin",
  });
};
</script>

<template>
  <div class="relative">
    <!-- Notification Bell Button -->
    <button
      class="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
      :class="{ 'bg-gray-100': isOpen }"
      @click="toggleDropdown"
    >
      <svg
        class="w-6 h-6"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
        />
      </svg>

      <!-- Unread Badge -->
      <span
        v-if="notificationsStore.unreadCount > 0"
        class="absolute top-0 right-0 flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full"
      >
        {{
          notificationsStore.unreadCount > 99
            ? "99+"
            : notificationsStore.unreadCount
        }}
      </span>
    </button>

    <!-- Dropdown -->
    <div
      v-if="isOpen"
      class="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50"
      @click.stop
    >
      <!-- Header -->
      <div class="p-4 border-b border-gray-200">
        <div class="flex items-center justify-between mb-3">
          <h3 class="text-lg font-semibold text-gray-900">
            Benachrichtigungen
          </h3>
          <button
            class="text-gray-400 hover:text-gray-600"
            @click="closeDropdown"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <!-- Tabs -->
        <div class="flex gap-2">
          <button
            class="px-3 py-1.5 text-sm font-medium rounded-md transition-colors"
            :class="
              activeTab === 'unread'
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            "
            @click="activeTab = 'unread'"
          >
            Ungelesen ({{ notificationsStore.unreadCount }})
          </button>
          <button
            class="px-3 py-1.5 text-sm font-medium rounded-md transition-colors"
            :class="
              activeTab === 'all'
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            "
            @click="activeTab = 'all'"
          >
            Alle
          </button>
        </div>
      </div>

      <!-- Actions -->
      <div
        v-if="notificationsStore.unreadCount > 0"
        class="px-4 py-2 border-b border-gray-200 bg-gray-50"
      >
        <button
          class="text-sm text-primary hover:text-primary-hover font-medium"
          @click="handleMarkAllAsRead"
        >
          Alle als gelesen markieren
        </button>
      </div>

      <!-- Notification List -->
      <div class="max-h-[300px] overflow-y-auto">
        <!-- Empty State -->
        <div v-if="filteredNotifications.length === 0" class="p-8 text-center">
          <svg
            class="w-12 h-12 mx-auto text-gray-400 mb-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
            />
          </svg>
          <p class="text-gray-500 text-sm">Keine Benachrichtigungen</p>
        </div>

        <!-- Notification Items -->
        <div
          v-for="notification in filteredNotifications"
          :key="notification.id"
          class="p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
          :class="{
            'bg-blue-50': notification.status === 'unread',
          }"
          @click="handleNotificationClick(notification)"
        >
          <div class="flex items-start gap-3">
            <!-- Icon -->
            <div
              class="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-xl"
              :class="[
                notification.status === 'unread'
                  ? 'bg-blue-100'
                  : 'bg-gray-100',
                getNotificationColor(notification.type),
              ]"
            >
              {{ getNotificationIcon(notification.type) }}
            </div>

            <!-- Content -->
            <div class="flex-1 min-w-0">
              <div class="flex items-start justify-between gap-2">
                <h4
                  class="text-sm font-semibold text-gray-900 truncate"
                  :class="{ 'font-bold': notification.status === 'unread' }"
                >
                  {{ notification.title }}
                </h4>
                <button
                  class="flex-shrink-0 text-gray-400 hover:text-red-600 transition-colors"
                  @click="handleDelete(notification.id, $event)"
                >
                  <svg
                    class="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              </div>
              <p class="text-sm text-gray-600 mt-1 line-clamp-2">
                {{ notification.message }}
              </p>
              <p class="text-xs text-gray-400 mt-1">
                {{ formatTimestamp(notification.createdAt) }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="p-3 border-t border-gray-200 bg-gray-50 text-center">
        <router-link
          to="/notifications"
          class="text-sm text-primary hover:text-primary-hover font-medium"
          @click="closeDropdown"
        >
          Alle Benachrichtigungen anzeigen
        </router-link>
      </div>
    </div>

    <!-- Backdrop -->
    <div v-if="isOpen" class="fixed inset-0 z-40" @click="closeDropdown"></div>
  </div>
</template>
